// /represent/:token — public representation confirm page.
//
// The player opens this from the email link; there is NO login (the
// unguessable, single-use token IS the auth). It reads the pending request,
// shows who wants to represent them and what that grants, and lets them
// Confirm or Decline. Standalone (outside AppShell) so a player arriving from
// email gets a clean branded page with no authed-nav fetch. Token-driven,
// accessible (h1 + named buttons + live status), both themes, mobile.

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Mark from '../components/Mark'
import Avatar from '../components/Avatar'
import FibaBadge from '../components/profile/FibaBadge'

interface RequestInfo {
  agent_name: string
  player_name: string
  expires_at: string
  agent: {
    id: string
    name: string
    is_fiba_licensed: boolean
    fiba_license_number: string | null
    bio: string | null
    photo_url: string | null
    slug: string | null
    agency: {
      id: string
      name: string
      country: string | null
      website: string | null
    } | null
  } | null
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; info: RequestInfo }
  | {
      kind: 'done'
      outcome: 'confirmed' | 'rejected'
      playerName: string | null
      playerSlug: string | null
    }

function errorMessage(key: string | undefined, status: number): string {
  switch (key) {
    case 'invalid_token':
      return 'This confirmation link is invalid. Check that you used the full link from your email.'
    case 'token_expired':
      return 'This confirmation link has expired. Representation links are valid for 7 days — ask the agent to send a new one.'
    case 'token_used':
      return 'This confirmation link has already been used.'
    case 'player_already_represented':
      return 'This player already has an agent on HoopRoster.'
    default:
      return `Something went wrong (${status}). Please try again.`
  }
}

export default function RepresentConfirm() {
  const { token } = useParams()
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    document.title = 'Confirm representation — HoopRoster'
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setState({ kind: 'error', message: errorMessage('invalid_token', 404) })
      return
    }
    async function load() {
      try {
        const res = await fetch(`/api/represent/${encodeURIComponent(token!)}`)
        const body = (await res.json().catch(() => null)) as
          | (Partial<RequestInfo> & { error?: string })
          | null
        if (cancelled) return
        if (res.ok && body && typeof body.agent_name === 'string') {
          setState({
            kind: 'ready',
            info: {
              agent_name: body.agent_name,
              player_name: body.player_name ?? 'this player',
              expires_at: body.expires_at ?? '',
              agent: body.agent ?? null,
            },
          })
        } else {
          setState({ kind: 'error', message: errorMessage(body?.error, res.status) })
        }
      } catch {
        if (!cancelled) {
          setState({ kind: 'error', message: 'Network error. Please try again.' })
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token])

  async function respond(action: 'confirm' | 'reject') {
    if (submitting || !token) return
    setSubmitting(true)
    try {
      const res = await fetch(`/api/represent/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      })
      const body = (await res.json().catch(() => null)) as
        | { status?: string; player_name?: string; player_slug?: string; error?: string }
        | null
      if (res.ok && (body?.status === 'confirmed' || body?.status === 'rejected')) {
        setState({
          kind: 'done',
          outcome: body.status,
          playerName: body.player_name ?? null,
          playerSlug: body.player_slug ?? null,
        })
      } else {
        setState({ kind: 'error', message: errorMessage(body?.error, res.status) })
      }
    } catch {
      setState({ kind: 'error', message: 'Network error. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-6 bg-hoop-bg">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Mark className="h-8 w-auto" />
          <span className="font-display font-semibold text-lg text-hoop-text">
            HoopRoster
          </span>
        </div>
        <div className="bg-hoop-surface border border-hoop-border rounded-2xl p-6 sm:p-7">
          {state.kind === 'loading' && (
            <p className="text-sm text-hoop-text-3" role="status">
              Loading…
            </p>
          )}

          {state.kind === 'error' && (
            <>
              <h1 className="text-xl font-bold text-hoop-text mb-2">
                Can't open this request
              </h1>
              <p className="text-sm text-hoop-text-2">{state.message}</p>
              <div className="mt-5">
                <Link to="/" className="text-sm text-hoop-accent hover:underline">
                  Go to HoopRoster →
                </Link>
              </div>
            </>
          )}

          {state.kind === 'ready' && (
            <>
              <h1 className="text-xl font-bold text-hoop-text">
                {state.info.agent_name} wants to represent you
              </h1>
              <p className="mt-2 text-sm text-hoop-text-2">
                <span className="font-medium text-hoop-text">
                  {state.info.agent_name}
                </span>{' '}
                has asked to be the agent for{' '}
                <span className="font-medium text-hoop-text">
                  {state.info.player_name}
                </span>{' '}
                on HoopRoster.
              </p>

              {state.info.agent && (
                <div className="mt-4 rounded-lg border border-hoop-border bg-hoop-bg p-4">
                  <div className="flex items-start gap-3">
                    <Avatar
                      src={state.info.agent.photo_url}
                      name={state.info.agent.name}
                      className="w-12 h-12 rounded-md border border-hoop-border flex-shrink-0"
                      textClassName="text-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-hoop-text">
                        {state.info.agent.name}
                      </div>
                      {state.info.agent.agency && (
                        <div className="text-xs text-hoop-text-3 mt-0.5">
                          {state.info.agent.agency.name}
                        </div>
                      )}
                      <div className="mt-1.5">
                        <FibaBadge
                          licenseNumber={state.info.agent.fiba_license_number}
                        />
                      </div>
                      {state.info.agent.slug && (
                        <a
                          href={`/agents/${state.info.agent.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block mt-2 text-xs text-hoop-accent hover:underline"
                        >
                          View full profile →
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-hoop-border bg-hoop-bg p-4 text-sm text-hoop-text-2">
                <div className="font-medium text-hoop-text mb-1">
                  Confirming lets {state.info.agent_name}:
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Manage your HoopRoster profile (details, stats, film)</li>
                  <li>Add you to their roster</li>
                  <li>Receive and respond to coach interest on your behalf</li>
                </ul>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => respond('confirm')}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md bg-hoop-primary text-hoop-primary-fg hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? 'Working…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => respond('reject')}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md border border-hoop-border bg-hoop-surface text-hoop-text hover:bg-hoop-bg disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
              <p className="mt-3 text-xs text-hoop-text-3">
                Didn't expect this? Declining is safe — nothing changes on the
                profile.
              </p>
            </>
          )}

          {state.kind === 'done' && state.outcome === 'confirmed' && (
            <>
              <h1 className="text-xl font-bold text-hoop-text">You're all set</h1>
              <p className="mt-2 text-sm text-hoop-text-2">
                Representation confirmed
                {state.playerName ? ` for ${state.playerName}` : ''}. Your agent
                can now manage the profile on HoopRoster.
              </p>
              {state.playerSlug && (
                <div className="mt-5">
                  <Link
                    to={`/players/${state.playerSlug}`}
                    className="text-sm text-hoop-accent hover:underline"
                  >
                    View the profile →
                  </Link>
                </div>
              )}
            </>
          )}

          {state.kind === 'done' && state.outcome === 'rejected' && (
            <>
              <h1 className="text-xl font-bold text-hoop-text">Request declined</h1>
              <p className="mt-2 text-sm text-hoop-text-2">
                No problem — the request was declined and nothing changed on the
                profile.
              </p>
              <div className="mt-5">
                <Link to="/" className="text-sm text-hoop-accent hover:underline">
                  Go to HoopRoster →
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
